import React, { useMemo, useState } from 'react';
import type { QuickMessageContent, QuickMessageResponse, QuickMessageScope } from '@troia-v3/types';
import { toast } from 'sonner';

import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { Button, Drawer, Tooltip } from '@/shared/components/ui';
import { usePermissions } from '@/hooks/usePermissions';

import { useDeleteQuickMessage, useQuickMessages } from '../hooks/useQuickMessages';
import QuickMessageForm from './QuickMessageForm';

interface QuickMessagesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Insert a quick message into the composer (text + staged media). */
  onInsert: (qm: QuickMessageResponse) => void;
}

type Tab = QuickMessageScope;
type View = 'list' | 'form';

const contentSummary = (content: QuickMessageContent[]): { icon: IconName | null; text: string } => {
  const textPart = content.find((c) => c.type === 'text');
  const media = content.find((c) => c.type !== 'text');
  const text = textPart && textPart.type === 'text' ? textPart.text : '';
  let icon: IconName | null = null;
  if (media) {
    icon =
      media.type === 'image'
        ? 'image'
        : media.type === 'video'
          ? 'video'
          : media.type === 'audio'
            ? 'music'
            : 'file';
  }
  return { icon, text };
};

const QuickMessagesDrawer: React.FC<QuickMessagesDrawerProps> = ({ isOpen, onClose, onInsert }) => {
  const [activeTab, setActiveTab] = useState<Tab>('personal');
  const [view, setView] = useState<View>('list');
  const [editing, setEditing] = useState<QuickMessageResponse | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const { hasModulePermission } = usePermissions();
  const { data, isLoading } = useQuickMessages(undefined, isOpen);
  const deleteMutation = useDeleteQuickMessage();

  const items = useMemo(() => data?.items ?? [], [data]);
  const visibleItems = useMemo(
    () => items.filter((qm) => qm.scope === activeTab),
    [items, activeTab],
  );

  // Company messages require the module permission to manage; personal are free.
  const canManageCompany = hasModulePermission('quick-messages', 'create');
  const canManageActive = activeTab === 'personal' || canManageCompany;

  const goToCreate = () => {
    setEditing(null);
    setView('form');
  };

  const goToEdit = (qm: QuickMessageResponse) => {
    setEditing(qm);
    setView('form');
  };

  const backToList = () => {
    setEditing(null);
    setView('list');
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Mensagem rápida removida');
    } catch {
      toast.error('Erro ao remover');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleClose = () => {
    setView('list');
    setEditing(null);
    setConfirmingId(null);
    onClose();
  };

  const footer =
    view === 'list' && canManageActive ? (
      <Button variant="primary" icon="plus" onClick={goToCreate} data-testid="quick-messages-new">
        Nova mensagem rápida
      </Button>
    ) : undefined;

  return (
    /* F56: header padrão do Drawer + prop `tabs` (abas hand-rolled do customHeader removidas) */
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      icon={<Icon name="zap" className="w-5 h-5" />}
      title={
        view === 'form'
          ? editing
            ? 'Editar mensagem rápida'
            : 'Nova mensagem rápida'
          : 'Mensagens rápidas'
      }
      subtitle="Respostas prontas para agilizar o atendimento"
      {...(view === 'list'
        ? {
            tabs: {
              items: [
                { id: 'personal', label: 'Minhas' },
                { id: 'company', label: 'Da empresa' },
              ],
              active: activeTab,
              onChange: (id: string) => {
                setActiveTab(id as Tab);
                setConfirmingId(null);
              },
            },
          }
        : {})}
      {...(footer ? { footer } : {})}
    >
      {view === 'form' ? (
        <QuickMessageForm
          scope={editing ? editing.scope : activeTab}
          editing={editing}
          onCancel={backToList}
          onSaved={backToList}
        />
      ) : (
        <div className="p-6" data-testid="quick-messages-list">
          {isLoading ? (
            <div className="text-center py-12 text-sm" style={{ color: '#94a3b8' }}>
              Carregando…
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="zap" className="w-10 h-10 mx-auto mb-3" style={{ color: '#cbd5e1' }} />
              <p className="text-sm" style={{ color: '#64748b' }}>
                {activeTab === 'personal'
                  ? 'Você ainda não criou mensagens rápidas'
                  : 'Nenhuma mensagem rápida da empresa'}
              </p>
              {!canManageActive && activeTab === 'company' && (
                <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                  Você não tem permissão para gerenciar mensagens da empresa
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {visibleItems.map((qm) => {
                const { icon, text } = contentSummary(qm.content);
                const isConfirming = confirmingId === qm.id;
                return (
                  <div
                    key={qm.id}
                    className="group flex items-center gap-3 p-3 rounded-lg border transition-colors"
                    style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}
                    data-testid={`quick-messages-item-${qm.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {qm.shortcut && (
                          <span
                            className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                            style={{ backgroundColor: '#ede9fe', color: '#7c3aed' }}
                          >
                            /{qm.shortcut}
                          </span>
                        )}
                        <span className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                          {qm.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {icon && <Icon name={icon} className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#8b5cf6' }} />}
                        {text && (
                          <span className="text-xs truncate" style={{ color: '#94a3b8' }}>
                            {text}
                          </span>
                        )}
                      </div>
                    </div>

                    {isConfirming ? (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs" style={{ color: '#64748b' }}>
                          Excluir?
                        </span>
                        <Button
                          variant="error"
                          size="xs"
                          onClick={() => handleDelete(qm.id)}
                          loading={deleteMutation.isPending}
                          data-testid={`quick-messages-confirm-delete-${qm.id}`}
                        >
                          Sim
                        </Button>
                        <Button variant="ghost" size="xs" onClick={() => setConfirmingId(null)}>
                          Não
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => {
                            onInsert(qm);
                            handleClose();
                          }}
                          data-testid={`quick-messages-use-${qm.id}`}
                        >
                          Usar
                        </Button>
                        {canManageActive && (
                          <>
                            <Tooltip content="Editar">
                              <button
                                type="button"
                                onClick={() => goToEdit(qm)}
                                className="p-1.5 rounded-md"
                                style={{ color: '#64748b' }}
                                data-testid={`quick-messages-edit-${qm.id}`}
                              >
                                <Icon name="edit" className="w-4 h-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content="Excluir">
                              <button
                                type="button"
                                onClick={() => setConfirmingId(qm.id)}
                                className="p-1.5 rounded-md"
                                style={{ color: '#64748b' }}
                                data-testid={`quick-messages-delete-${qm.id}`}
                              >
                                <Icon name="trash" className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

QuickMessagesDrawer.displayName = 'QuickMessagesDrawer';

export default QuickMessagesDrawer;
